import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Label,
} from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { useEffect, useState } from 'react';
import { useOrgUserStore } from '../../../shared/organization/orgUserStore';
import { getOrgMembersAction } from '../../../shared/organization/organizationActions';
import { useAuthStore } from '../../auth/authStore';
import { OrganizationUser } from '../../../shared/organization/types';
import { DiagramFormData, DiagramFormUser } from './types';

interface Props {
  setFormData: React.Dispatch<React.SetStateAction<DiagramFormData>>;
  users: DiagramFormUser[];
}

// REFACTOR: Make this a generic component
const UserComboBox = ({ setFormData, users }: Props) => {
  const [query, setQuery] = useState('');
  const items = useOrgUserStore((state) => state.items);
  const auth = useAuthStore((state) => state.auth);
  const [filteredUsers, setFilteredUsers] = useState<DiagramFormUser[]>([]);

  const filteredMembers = Object.values(items).filter((member) => {
    if (auth) {
      const isNotCurrentUser = member.uid !== auth.uid;
      const isNotInUsersProp = users
        ? !users.some((user) => user.uid === member.uid)
        : true;
      // Check if the user is not in the users prop
      return isNotCurrentUser && isNotInUsersProp;
    }
    return false;
  });
  const [selectedUser, setSelectedUser] = useState<OrganizationUser | null>(
    null,
  );

  useEffect(() => {
    setFilteredUsers(
      Object.values(items)
        .filter((member) => {
          if (auth) {
            const isNotCurrentUser = member.uid !== auth.uid;
            const isNotInUsersProp = users
              ? !users.some((user) => user.uid === member.uid)
              : true;
            // Check if the user is not in the users prop
            return isNotCurrentUser && isNotInUsersProp;
          }
          return false;
        })
        .map(
          (member) =>
            ({
              ...member,
              role: 'viewer' as 'viewer' | 'editor',
              orgRole: member.role,
            }) as DiagramFormUser,
        ),
    );
  }, [users, auth, items]);

  useEffect(
    () => {
      getOrgMembersAction();
    },
    // eslint-disable-next-line
    [],
  );
  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  const handleSelection = (member: OrganizationUser | null) => {
    if (member) {
      setSelectedUser(member);
      setFormData((prev: DiagramFormData) => ({
        ...prev,
        users: [
          ...prev.users,
          //FIXME
          { ...member, role: 'viewer', orgRole: 'editor' },
        ],
      }));
      setSelectedUser(null);
    }
  };

  const searchedMembers = filteredUsers.filter((member) =>
    member.displayName.toLowerCase().includes(query.toLowerCase()),
  );

  return (
    <Combobox
      as="div"
      value={selectedUser}
      onChange={handleSelection}
      className="w-full"
    >
      <Label className="block text-sm/6 font-medium text-gray-900">
        Members
      </Label>
      <div className="relative mt-2 w-full">
        <ComboboxInput
          className="block w-full rounded-md bg-white py-1.5 pr-12 pl-3 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:text-sm/6 disabled:opacity-50"
          onChange={handleInputChange}
          onBlur={() => setQuery('')}
          displayValue={(member: OrganizationUser) => member?.displayName}
          disabled={filteredMembers.length === 0}
        />
        <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-hidden">
          <ChevronUpDownIcon
            className="size-5 text-gray-400"
            aria-hidden="true"
          />
        </ComboboxButton>
        <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base ring-1 shadow-lg ring-black/5 focus:outline-hidden sm:text-sm">
          {searchedMembers.length > 0 ? (
            searchedMembers.map((member) => (
              <ComboboxOption
                key={member.id}
                value={member}
                className="group relative cursor-default py-2 pr-9 pl-3 text-gray-900 select-none data-focus:bg-primary data-focus:text-white data-focus:outline-hidden"
              >
                <span className="block truncate group-data-selected:font-semibold">
                  {member.displayName}
                </span>

                <span className="absolute inset-y-0 right-0 hidden items-center pr-4 text-primary group-data-focus:text-white group-data-selected:flex">
                  <CheckIcon className="size-5" aria-hidden="true" />
                </span>
              </ComboboxOption>
            ))
          ) : (
            <div className="px-3 py-2 text-gray-500">
              {filteredMembers.length === 0
                ? 'No organization members, invite some to get started!'
                : 'No members found'}
            </div>
          )}
        </ComboboxOptions>
      </div>
    </Combobox>
  );
};
export default UserComboBox;
